import { useCallback, useEffect, useState } from "react"
import {
  getDailyWellnessRange,
  getTrainingLoads,
  getTodayWellness,
  getProgrammeSetLogs,
  type DailyWellnessRow,
  type TrainingLoadRow,
  type ProgrammeSetLogs,
} from "@/lib/supabase"
import {
  computeACWR,
  computeStrain,
  computeProgressScore,
  computeIntensityTrend,
  computeVolumeTrend,
  trendForScore,
  getHooperZone,
} from "@/lib/monitoring/calculations"

export function useMonitoring(days: number = 30) {
  const [wellness, setWellness] = useState<DailyWellnessRow[]>([])
  const [loads, setLoads] = useState<TrainingLoadRow[]>([])
  const [setLogs, setSetLogs] = useState<ProgrammeSetLogs>({ plan: [], logs: [] })
  const [todayWellness, setTodayWellness] = useState<DailyWellnessRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [w, l, sl, t] = await Promise.all([
        getDailyWellnessRange(days),
        // computeACWR a besoin de 56 jours d'amorçage EWMA en interne, indépendamment de `days`
        // (fenêtre d'affichage) — voir calculations.ts.
        getTrainingLoads(Math.max(100, days + 56)),
        // Par semaine de PROGRAMME, pas par fenêtre de jours (cf. getProgrammeSetLogs).
        getProgrammeSetLogs(),
        getTodayWellness(),
      ])
      setWellness(w)
      setLoads(l)
      setSetLogs(sl)
      setTodayWellness(t)
    } catch (err) {
      console.error("[useMonitoring] error:", err)
      setError(err instanceof Error ? err.message : "Erreur chargement")
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Calculs dérivés
  const acwr = computeACWR(loads)
  const strain = computeStrain(loads)
  const intensity = computeIntensityTrend(setLogs.logs, setLogs.plan)
  const volume = computeVolumeTrend(setLogs.logs, setLogs.plan)
  // trendForScore : verdict de la dernière semaine de programme COMPLÈTE ; une
  // reprise post-décharge ne peut pas faire baisser le score (trend négatif
  // neutralisé), mais peut le faire monter (trend positif compté).
  const progress = computeProgressScore(wellness, trendForScore(intensity), trendForScore(volume))
  const todayHooper = todayWellness
    ? { score: todayWellness.hooper_score, ...getHooperZone(todayWellness.hooper_score) }
    : null

  // Moyenne hooper 7 derniers jours
  const today = new Date()
  const sevenDaysAgo = new Date(today)
  sevenDaysAgo.setDate(today.getDate() - 7)
  const last7Wellness = wellness.filter((w) => new Date(w.date) >= sevenDaysAgo)
  const avg7Hooper =
    last7Wellness.length > 0
      ? Math.round(
          last7Wellness.reduce((s, w) => s + w.hooper_score, 0) / last7Wellness.length
        )
      : null

  return {
    wellness,
    loads,
    setLogs,
    todayWellness,
    todayHooper,
    avg7Hooper,
    acwr,
    strain,
    progress,
    intensity,
    volume,
    loading,
    error,
    refresh,
  }
}
